import { useEffect, useRef } from 'react';
import { metaData, Enums as CoreEnums } from '@cornerstonejs/core';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import {
  resolveSpectralRegion,
  buildCalibrationFromRegion,
} from '@/features/study-detail/Viewer/tools/SpectralDoppler/calibrationRegion';
import { pixelToWorld } from '@/features/study-detail/Viewer/tools/SpectralDoppler/worldPixelCoords';
import { SPECTRAL_DOPPLER_TOOL_NAMES } from '@/models/spectralDoppler';
import type { UltrasoundRegionsMeta } from '@/features/study-detail/Viewer/tools/SpectralDoppler/SpectralDopplerBaseTool';

interface UseDopplerBaselineOverlayProps {
  viewport:
    | CornerstoneTypes.IStackViewport
    | CornerstoneTypes.IVideoViewport
    | null;
  /**
   * Re-evaluate when the displayed frame/image changes (cine playback or
   * scroll). The hook reads getCurrentImageId() at render time, so the
   * dependency just needs something that changes per frame.
   */
  currentFrame: number;
  /**
   * Currently active tool name (from ViewerContext). When the VTI tool is
   * active the overlay swaps to a dashed guide and renders an X marker that
   * tracks the mouse pointer along the baseline.
   */
  activeTool: string | undefined;
}

const OVERLAY_CLASS = 'doppler-baseline-overlay-canvas';
// Resolved against the viewport element so theme switches flow through
// automatically. Fallbacks match the token defaults for the rare case
// getComputedStyle returns empty (e.g. detached element).
// The drawing guide always uses the manual colour.
const GUIDE_COLOR_VAR = '--annotation-manual-active';
const GUIDE_COLOR_FALLBACK = '#1998e9';
const BASELINE_WIDTH = 1.5;
const VTI_DASH_PATTERN = [6, 4];
const X_MARKER_HALF = 6;
const X_MARKER_WIDTH = 2;

function resolveCssColor(
  element: HTMLElement,
  varName: string,
  fallback: string
): string {
  const value = getComputedStyle(element).getPropertyValue(varName).trim();
  return value || fallback;
}

/**
 * Image-pixel anchors of the spectral region's baseline. These come from
 * DICOM ultrasound region metadata and are camera-independent. World coords
 * are NOT cached here because VideoViewport's `worldToCanvas` mapping is
 * defined relative to the live canvas size: `refreshRenderValues()` rebuilds
 * `panWorld` and `worldToCanvasRatio` from `canvas.offsetWidth/Height` on
 * every resize, so the same image pixel maps to *different* world coords
 * before and after a resize. We re-resolve pixel → world → canvas inside
 * render() so all three projections share one consistent camera state.
 */
interface BaselinePixelAnchors {
  leftPixelX: number;
  rightPixelX: number;
  baselinePixelY: number;
}

/**
 * Paints a non-interactive drawing aid on a canvas above the Cornerstone
 * viewport while the VTI tool is active — visual only: a dashed full-width line
 * along the Doppler zero-velocity baseline (`referencePixelY0`) plus an X
 * marker tracking the mouse pointer's x. Nothing is drawn on the baseline for
 * finished traces.
 *
 * No-ops on non-Doppler viewports. Mirrors the canvas-overlay lifecycle
 * used by useSimpsonOverlay so the two overlays compose without stomping.
 */
export function useDopplerBaselineOverlay({
  viewport,
  currentFrame,
  activeTool,
}: UseDopplerBaselineOverlayProps): void {
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pixelAnchorsRef = useRef<BaselinePixelAnchors | null>(null);
  const mouseXRef = useRef<number | null>(null);
  const renderRef = useRef<(() => void) | null>(null);

  const isVTIActive = activeTool === SPECTRAL_DOPPLER_TOOL_NAMES.VTI;

  useEffect(() => {
    if (!viewport) return undefined;

    const viewportElement = viewport.element as HTMLElement;
    if (!viewportElement) return undefined;

    const removeOverlay = () => {
      const existing = viewportElement.querySelector<HTMLCanvasElement>(
        `.${OVERLAY_CLASS}`
      );
      if (existing) existing.remove();
      overlayCanvasRef.current = null;
      pixelAnchorsRef.current = null;
      renderRef.current = null;
    };

    const imageId =
      (
        viewport as unknown as {
          getCurrentImageId?: () => string | undefined;
        }
      ).getCurrentImageId?.() ?? '';
    if (!imageId) {
      removeOverlay();
      return undefined;
    }

    const regionsMeta = metaData.get('ultrasoundRegions', imageId) as
      | UltrasoundRegionsMeta
      | undefined;
    const { region, reason } = resolveSpectralRegion(regionsMeta?.regions);
    if (reason !== 'ok' || !region) {
      removeOverlay();
      return undefined;
    }

    const calibration = buildCalibrationFromRegion(region);

    let overlay = viewportElement.querySelector<HTMLCanvasElement>(
      `.${OVERLAY_CLASS}`
    );
    if (!overlay) {
      overlay = document.createElement('canvas');
      overlay.className = OVERLAY_CLASS;
      overlay.style.position = 'absolute';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.pointerEvents = 'none';
      // Beneath simpson-overlay (z-index 10) so a Simpson trace can sit on
      // top of the baseline guide if both ever coexist.
      overlay.style.zIndex = '9';
      viewportElement.appendChild(overlay);
    }

    overlayCanvasRef.current = overlay;
    // Cache only the image-pixel anchors — these come from DICOM region
    // metadata and don't depend on the viewport's camera state. World coords
    // are re-derived inside render() against the live camera.
    pixelAnchorsRef.current = {
      leftPixelX: calibration.bboxMinX,
      rightPixelX: calibration.bboxMaxX,
      baselinePixelY: calibration.referencePixelY0,
    };

    const render = () => {
      const canvas = overlayCanvasRef.current;
      const anchors = pixelAnchorsRef.current;
      if (!canvas || !anchors) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Sync canvas size to viewport every render — handles container
      // resizes (window resize, layout shifts) without a separate observer.
      const rect = viewportElement.getBoundingClientRect();
      if (canvas.width !== rect.width) canvas.width = rect.width;
      if (canvas.height !== rect.height) canvas.height = rect.height;

      // Re-derive world coords from the camera-invariant pixel anchors on
      // every paint, then project to canvas. Doing pixel → world → canvas
      // in one shot guarantees all three projections share the *current*
      // camera state — critical for VideoViewport, where the world-space
      // origin shifts with canvas size.
      const leftWorld = pixelToWorld(
        viewport,
        anchors.leftPixelX,
        anchors.baselinePixelY
      );
      const rightWorld = pixelToWorld(
        viewport,
        anchors.rightPixelX,
        anchors.baselinePixelY
      );
      if (!leftWorld || !rightWorld) return;

      const vp = viewport as unknown as {
        worldToCanvas: (w: CornerstoneTypes.Point3) => CornerstoneTypes.Point2;
      };
      const leftCanvas = vp.worldToCanvas(leftWorld);
      const rightCanvas = vp.worldToCanvas(rightWorld);
      const leftCanvasX = leftCanvas[0];
      const rightCanvasX = rightCanvas[0];
      // Average the two transformed Y values; they match in practice
      // because the baseline is a horizontal pixel row.
      const baselineY = (leftCanvas[1] + rightCanvas[1]) / 2;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Drawing aid — only while the user is actively using the VTI tool.
      if (isVTIActive) {
        const guideColor = resolveCssColor(
          viewportElement,
          GUIDE_COLOR_VAR,
          GUIDE_COLOR_FALLBACK
        );
        ctx.strokeStyle = guideColor;
        ctx.lineWidth = BASELINE_WIDTH;
        ctx.setLineDash(VTI_DASH_PATTERN);
        ctx.beginPath();
        ctx.moveTo(leftCanvasX, baselineY);
        ctx.lineTo(rightCanvasX, baselineY);
        ctx.stroke();
        ctx.setLineDash([]);

        if (mouseXRef.current !== null) {
          const clampedX = Math.max(
            leftCanvasX,
            Math.min(rightCanvasX, mouseXRef.current)
          );
          ctx.strokeStyle = guideColor;
          ctx.lineWidth = X_MARKER_WIDTH;
          ctx.beginPath();
          ctx.moveTo(clampedX - X_MARKER_HALF, baselineY - X_MARKER_HALF);
          ctx.lineTo(clampedX + X_MARKER_HALF, baselineY + X_MARKER_HALF);
          ctx.moveTo(clampedX + X_MARKER_HALF, baselineY - X_MARKER_HALF);
          ctx.lineTo(clampedX - X_MARKER_HALF, baselineY + X_MARKER_HALF);
          ctx.stroke();
        }
      }
    };

    renderRef.current = render;
    render();

    return undefined;
  }, [viewport, currentFrame, isVTIActive]);

  // Re-render on every viewport repaint. Two events cover the cases we care
  // about:
  //
  // - CAMERA_MODIFIED: fires for stack viewports on zoom/pan/resize. Stack
  //   viewports rely on this for annotation re-rendering.
  //
  // - IMAGE_RENDERED: fires from VideoViewport.renderFrame() *after*
  //   refreshRenderValues() has updated panWorld + worldToCanvasRatio.
  //   This matters because VideoViewport overrides resetCamera() and does
  //   NOT fire CAMERA_MODIFIED — so resize never reaches our overlay
  //   through the camera event for Doppler clips. IMAGE_RENDERED also
  //   fires per video frame during playback, but render() is cheap canvas
  //   work and the redraw is harmless.
  //
  // Both listeners feed the same render() call, so double-fires (e.g. for
  // a stack viewport that emits both) are idempotent.
  useEffect(() => {
    if (!viewport) return undefined;
    const viewportElement = viewport.element as HTMLElement;
    if (!viewportElement) return undefined;
    const handler = () => {
      renderRef.current?.();
    };
    viewportElement.addEventListener(CoreEnums.Events.CAMERA_MODIFIED, handler);
    viewportElement.addEventListener(CoreEnums.Events.IMAGE_RENDERED, handler);
    return () => {
      viewportElement.removeEventListener(
        CoreEnums.Events.CAMERA_MODIFIED,
        handler
      );
      viewportElement.removeEventListener(
        CoreEnums.Events.IMAGE_RENDERED,
        handler
      );
    };
  }, [viewport]);

  // Track the mouse pointer's x position over the viewport while the VTI
  // tool is active so the X marker can follow it along the baseline.
  useEffect(() => {
    if (!isVTIActive || !viewport) return undefined;
    const viewportElement = viewport.element as HTMLElement;
    if (!viewportElement) return undefined;

    const handleMove = (e: MouseEvent) => {
      const rect = viewportElement.getBoundingClientRect();
      mouseXRef.current = e.clientX - rect.left;
      renderRef.current?.();
    };
    const handleLeave = () => {
      mouseXRef.current = null;
      renderRef.current?.();
    };

    viewportElement.addEventListener('mousemove', handleMove);
    viewportElement.addEventListener('mouseleave', handleLeave);

    return () => {
      viewportElement.removeEventListener('mousemove', handleMove);
      viewportElement.removeEventListener('mouseleave', handleLeave);
      mouseXRef.current = null;
      renderRef.current?.();
    };
  }, [isVTIActive, viewport]);

  // Clean up overlay when the viewport changes / unmounts.
  useEffect(
    () => () => {
      if (overlayCanvasRef.current) {
        overlayCanvasRef.current.remove();
        overlayCanvasRef.current = null;
      }
      pixelAnchorsRef.current = null;
      renderRef.current = null;
      mouseXRef.current = null;
    },
    [viewport]
  );
}
